import { useGetImmaginiIdFile } from '@framework/api-client/api/procedures/immagine-controller/immagine-controller.ts';
import { useEffect, useState } from 'react';

interface IImageFileFullResponse {
  data: ArrayBuffer;
  headers: Record<string, string>;
}

export interface IUseImagePreviewUrlResult {
  url: string | undefined;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Recupera il binario di un'immagine tramite l'apiClient autenticato
 * (l'header `Authorization: Bearer` viene aggiunto dall'interceptor)
 * e ne costruisce un object URL utilizzabile come `src` di un `<img>`.
 *
 * Una semplice richiesta `<img src>` bypasserebbe l'apiClient e quindi
 * il token, ricevendo un 403 dall'endpoint autenticato.
 *
 * L'object URL viene gestito tramite stato ed effetto, revocandolo in
 * cleanup e a ogni cambio di id/dati per evitare leak e URL stantii.
 */
export function useImagePreviewUrl(
  imageId: string | null | undefined,
): IUseImagePreviewUrlResult {
  const { data, isLoading, isError } = useGetImmaginiIdFile(imageId ?? '', {
    request: { responseType: 'arraybuffer', fullResponse: true },
    query: { enabled: !!imageId },
  });

  const [url, setUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!data) {
      setUrl(undefined);
      return;
    }

    const { data: binary, headers } = data as unknown as IImageFileFullResponse;

    if (!binary) {
      setUrl(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(
      new Blob([binary], { type: headers?.['content-type'] }),
    );
    setUrl(objectUrl);

    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [data]);

  return { url, isLoading, isError };
}
